import { Swiper } from 'swiper';
import { Navigation, Pagination, Autoplay, EffectFade } from 'swiper/modules';

class HeroSwiper {
  constructor() {
    if (HeroSwiper.instance) {
      return HeroSwiper.instance;
    }

    this.initialized = false;
    this.init();
    HeroSwiper.instance = this;
  }

  init() {
    if (this.initialized) {
      return;
    }

    const swiperContainers = document.querySelectorAll('.hero-swiper');
    if (swiperContainers.length === 0) return;

    swiperContainers.forEach(container => {
      this.initSwiper(container);
    });

    this.initialized = true;
  }

  initSwiper(container) {
    // Prevent multiple initializations
    if (container.classList.contains('swiper-initialized') || container.swiper) {
      return;
    }

    // Add unique identifier to prevent duplicate processing
    if (container.dataset.swiperProcessing) {
      return;
    }
    container.dataset.swiperProcessing = 'true';

    const configData = container.getAttribute('data-swiper-config');
    if (!configData) return;

    let config;
    try {
      config = JSON.parse(configData);
    } catch (e) {
      console.error('Invalid Swiper config:', e);
      return;
    }

    if (config.autoplay && config.autoplay.delay) {
		const autoplayDelay = config.autoplay.delay;
		container.style.setProperty('--swiper-autoplay-delay', `${autoplayDelay}ms`);
	}

    const slides = container.querySelectorAll('.swiper-slide');
    if (slides.length === 0) {
      return;
    }

    const modules = [Pagination];
    if (config.autoplay) modules.push(Autoplay);
    if (config.navigation) modules.push(Navigation);
    if (config.effect === 'fade') modules.push(EffectFade);

    setTimeout(() => {
      const paginationEl = container.querySelector('.swiper-pagination');
      if (!paginationEl) {
        return;
      }

      let isInitialized = false;
      let lastSlideChangeTime = 0;
      let isProcessingSlideChange = false;
      let lastProcessedIndex = -1;
      let isFirstLoad = true;

      // Function to update bullet states
      function updateBulletStates(container, currentIndex, isInitialLoad = false) {
        const allBullets = container.querySelectorAll('.swiper-pagination-bullet');

        allBullets.forEach((bullet, index) => {
          // Remove only our custom classes, keep Swiper's classes
          bullet.classList.remove('swiper-pagination-bullet-completed', 'animate-progress');

          if (index < currentIndex) {
            // Previous slides - fully blue (completed)
            bullet.classList.add('swiper-pagination-bullet-completed');
          } else if (index === currentIndex) {
            // Current slide - active (Swiper handles the swiper-pagination-bullet-active class)

            // Add animation class only if autoplay is enabled
            if (container.classList.contains('autoplay-enabled')) {
              // For initial load, start immediately. For slide changes, wait for transition
              const transitionDelay = isInitialLoad ? 0 : (config.speed || 500);
              container.style.setProperty('--swiper-transition-delay', `${transitionDelay}ms`);

              // Add reset class to force pseudo-element reset
              bullet.classList.add('reset-animation');
              bullet.offsetHeight; // Force reflow
              bullet.classList.remove('reset-animation');

              // For initial load, start immediately. For slide changes, add delay
              const delay = isInitialLoad ? 0 : 100;
              setTimeout(() => {
                bullet.classList.add('animate-progress');
              }, delay);
            }
          } else {
            // Future slides - gray (inactive)
          }
        });
      }

      // We merge our custom `on` events with any that might already exist in the original config.
      const finalOnEvents = {
        ...(config.on || {}),
        init: function() {
          container.classList.add('swiper-initialized');

          // Set CSS custom properties for animation timing and autoplay class
          const autoplayDelay = config.autoplay ? config.autoplay.delay : 5000;
          const transitionSpeed = config.speed || 500;

          container.style.setProperty('--swiper-autoplay-delay', `${autoplayDelay}ms`);
          container.style.setProperty('--swiper-transition-speed', `${transitionSpeed}ms`);
          container.style.setProperty('--swiper-transition-delay', `${transitionSpeed}ms`);

          // Add autoplay-enabled class to container if autoplay is enabled
          if (config.autoplay) {
            container.classList.add('autoplay-enabled');
          }

          setTimeout(() => {
            isInitialized = true;
            // Clear processing flag
            delete container.dataset.swiperProcessing;

            // Set initial bullet states with immediate start for first load
            const currentIndex = this.params.loop ? this.realIndex : this.activeIndex;
            updateBulletStates(container, currentIndex, true); // true = isInitialLoad
          }, 100);
        },
        slideChange: function() {
          if (isInitialized && !isProcessingSlideChange) {
            const now = Date.now();

            // Use realIndex for loop mode, activeIndex for non-loop mode
            const currentIndex = this.params.loop ? this.realIndex : this.activeIndex;

            // Check if this is the same slide as the last processed one
            if (currentIndex === lastProcessedIndex) {
              return;
            }

            // Debounce rapid slideChange events
            if (now - lastSlideChangeTime < 500) {
              return;
            }

            isProcessingSlideChange = true;
            lastSlideChangeTime = now;
            lastProcessedIndex = currentIndex;

            // Update bullet states - use isFirstLoad flag for timing
            updateBulletStates(container, currentIndex, isFirstLoad);
            isFirstLoad = false; // Mark that we've processed the first slide change

            // Reset processing flag after a short delay
            setTimeout(() => {
              isProcessingSlideChange = false;
            }, 100);
          }
        }
      };

      const swiperInstance = new Swiper(container, {
        ...config, // Pass the original config
        modules,
        on: finalOnEvents, // Use the merged event handlers
        watchSlidesProgress: true,
        watchSlidesVisibility: true,
        observer: true,
        observeParents: true,
      });

      container.swiper = swiperInstance;
    }, 100);
  }
}

// Export for use in main.js
export default function initHeroSwiper() {
  new HeroSwiper();
}

// Re-initialize for dynamic content (ACF preview, etc.)
if (typeof acf !== 'undefined') {
  acf.addAction('render_block_preview/type=hero-swiper', () => {
    document.querySelectorAll('.hero-swiper.swiper-initialized').forEach(container => {
      if (container.swiper) {
        container.swiper.destroy(true, true);
        container.classList.remove('swiper-initialized');
        container.swiper = null;
        delete container.dataset.swiperProcessing;
      }
    });
    setTimeout(() => {
      // Reset the singleton instance and reinitialize
      HeroSwiper.instance = null;
      initHeroSwiper();
    }, 100);
  });
}